import React, { useState } from 'react';
import { DollarSign, ArrowDownCircle, Star, Users, Plus, X, ChevronLeft, ChevronRight, LayoutList, CalendarDays } from 'lucide-react';
import { useUserData } from '../context/UserDataContext';
import EmptyState from './EmptyState';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Math.abs(n));
const TODAY = new Date().toISOString().slice(0, 10);

const TYPE_CONFIG = {
  income:  { icon: <DollarSign size={14} />,      color: 'text-emerald-400', bg: 'bg-emerald-950/50 border-emerald-700/40', dot: 'bg-emerald-400', label: 'Income',   gridDot: 'bg-emerald-400' },
  bill:    { icon: <ArrowDownCircle size={14} />,  color: 'text-rose-400',   bg: 'bg-rose-950/50 border-rose-700/40',     dot: 'bg-rose-400',   label: 'Bill',     gridDot: 'bg-rose-400'   },
  rsu:     { icon: <Star size={14} />,             color: 'text-purple-300', bg: 'bg-purple-950/50 border-purple-700/40', dot: 'bg-purple-400', label: 'RSU Vest', gridDot: 'bg-purple-400' },
  meeting: { icon: <Users size={14} />,            color: 'text-blue-300',   bg: 'bg-blue-950/50 border-blue-700/40',     dot: 'bg-blue-400',   label: 'Meeting',  gridDot: 'bg-blue-400'   },
  goal:    { icon: <Star size={14} />,             color: 'text-blue-400',   bg: 'bg-blue-950/50 border-blue-700/40',     dot: 'bg-blue-400',   label: 'Goal',     gridDot: 'bg-blue-400'   },
};

function groupByMonth(events) {
  const groups = {};
  events.forEach(ev => {
    const key = ev.date.slice(0, 7);
    if (!groups[key]) groups[key] = [];
    groups[key].push(ev);
  });
  return groups;
}

const EMPTY_FORM = { date: TODAY, type: 'bill', title: '', amount: '', recurrence: '', note: '' };

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ── Month Grid View ───────────────────────────────────────────────────────────
function MonthGrid({ events, onAddClick }) {
  const [viewDate, setViewDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);

  const year  = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayStr = new Date().toISOString().slice(0, 10);

  // Build day cells (blank + day 1..N)
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  // Map events by date string
  const eventsByDate = {};
  events.forEach(ev => {
    if (!eventsByDate[ev.date]) eventsByDate[ev.date] = [];
    eventsByDate[ev.date].push(ev);
  });

  const toDateStr = (d) => {
    const m = String(month + 1).padStart(2, '0');
    const day = String(d).padStart(2, '0');
    return `${year}-${m}-${day}`;
  };

  const prevMonth = () => setViewDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const nextMonth = () => setViewDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));

  const monthName = viewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const selectedDateStr = selectedDay ? toDateStr(selectedDay) : null;
  const selectedEvents  = selectedDateStr ? (eventsByDate[selectedDateStr] || []) : [];

  return (
    <div className="space-y-4">
      {/* Month navigation */}
      <div className="flex items-center justify-between">
        <button onClick={prevMonth} className="p-2 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors">
          <ChevronLeft size={18} />
        </button>
        <h2 className="font-semibold text-slate-200 text-base">{monthName}</h2>
        <button onClick={nextMonth} className="p-2 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors">
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs">
        {Object.entries(TYPE_CONFIG).filter(([k]) => k !== 'goal').map(([type, cfg]) => (
          <div key={type} className="flex items-center gap-1.5">
            <div className={`w-2.5 h-2.5 rounded-full ${cfg.gridDot}`} />
            <span className="text-slate-400">{cfg.label}</span>
          </div>
        ))}
      </div>

      {/* Grid */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
        {/* Day-of-week headers */}
        <div className="grid grid-cols-7 border-b border-slate-700">
          {DAYS_OF_WEEK.map(d => (
            <div key={d} className="py-2 text-center text-xs font-medium text-slate-500">{d}</div>
          ))}
        </div>

        {/* Date cells */}
        <div className="grid grid-cols-7">
          {cells.map((day, idx) => {
            if (day === null) {
              return <div key={`blank-${idx}`} className="min-h-[72px] border-b border-r border-slate-700/40 bg-slate-900/20" />;
            }
            const dateStr   = toDateStr(day);
            const dayEvents = eventsByDate[dateStr] || [];
            const isToday   = dateStr === todayStr;
            const isSelected = selectedDay === day;
            const isPast    = dateStr < todayStr;

            return (
              <button
                key={day}
                onClick={() => setSelectedDay(isSelected ? null : day)}
                className={`min-h-[72px] p-2 border-b border-r border-slate-700/40 text-left transition-all relative ${
                  isSelected
                    ? 'bg-blue-900/30 border-blue-700/40'
                    : 'hover:bg-slate-700/30'
                } ${isPast && !isToday ? 'opacity-60' : ''}`}
              >
                <div className={`text-xs font-semibold mb-1 w-6 h-6 flex items-center justify-center rounded-full ${
                  isToday
                    ? 'bg-blue-600 text-white'
                    : isSelected
                    ? 'text-blue-300'
                    : 'text-slate-400'
                }`}>
                  {day}
                </div>
                {/* Event dots */}
                <div className="flex flex-wrap gap-0.5">
                  {dayEvents.slice(0, 4).map((ev, i) => {
                    const cfg = TYPE_CONFIG[ev.type] || TYPE_CONFIG.bill;
                    return (
                      <div
                        key={i}
                        className={`w-1.5 h-1.5 rounded-full ${cfg.gridDot}`}
                        title={ev.title}
                      />
                    );
                  })}
                  {dayEvents.length > 4 && (
                    <span className="text-xs text-slate-500">+{dayEvents.length - 4}</span>
                  )}
                </div>
                {/* Show first event title on larger screens */}
                {dayEvents.length > 0 && (
                  <div className="hidden sm:block text-xs text-slate-500 truncate leading-tight mt-0.5">
                    {dayEvents[0].title}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected day events */}
      {selectedDay && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-slate-200 text-sm">
              {new Date(toDateStr(selectedDay) + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </h3>
            <button
              onClick={() => { onAddClick(toDateStr(selectedDay)); }}
              className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors"
            >
              <Plus size={12} /> Add event
            </button>
          </div>
          {selectedEvents.length === 0 ? (
            <p className="text-xs text-slate-500 text-center py-4">No events. Click "Add event" to schedule something.</p>
          ) : (
            <div className="space-y-2">
              {selectedEvents.map((ev, i) => {
                const t = TYPE_CONFIG[ev.type] || TYPE_CONFIG.bill;
                return (
                  <div key={i} className={`flex items-center gap-3 p-2.5 rounded-lg border ${t.bg}`}>
                    <div className={`${t.color}`}>{t.icon}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-200 truncate">{ev.title}</div>
                      {ev.note && <div className="text-xs text-slate-500">{ev.note}</div>}
                    </div>
                    {ev.amount !== 0 && (
                      <div className={`text-sm font-bold flex-shrink-0 ${ev.amount > 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {ev.amount > 0 ? '+' : '-'}{fmt(ev.amount)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Calendar component ───────────────────────────────────────────────────
export default function Calendar() {
  const { calendarData, updateSection } = useUserData();
  const [filter,   setFilter]   = useState('all');
  const [showAdd,  setShowAdd]  = useState(false);
  const [form,     setForm]     = useState(EMPTY_FORM);
  const [viewMode, setViewMode] = useState('month'); // 'month' | 'list'

  const data     = calendarData || [];
  const filtered = filter === 'all' ? data : data.filter(ev => ev.type === filter);
  const grouped  = groupByMonth(filtered);

  const monthlyIncome = data.filter(e => e.type === 'income').reduce((s, e) => s + (e.amount || 0), 0);
  const monthlyBills  = data.filter(e => e.type === 'bill').reduce((s, e) => s + (e.amount || 0), 0);
  const rsuVests      = data.filter(e => e.type === 'rsu').reduce((s, e) => s + (e.amount || 0), 0);

  const monthLabel = (key) => {
    const [y, m] = key.split('-');
    return new Date(y, m - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const handleAdd = (prefillDate) => {
    if (!form.title.trim() || !form.date) return;
    const newEvent = {
      id:         Date.now().toString(),
      date:       form.date,
      type:       form.type,
      title:      form.title.trim(),
      amount:     parseFloat(form.amount) || 0,
      recurrence: form.recurrence || undefined,
      note:       form.note.trim() || undefined,
    };
    updateSection('calendar', [...data, newEvent]);
    setForm(EMPTY_FORM);
    setShowAdd(false);
  };

  const openAddForDate = (date) => {
    setForm({ ...EMPTY_FORM, date });
    setShowAdd(true);
  };

  const handleDelete = (id) => {
    updateSection('calendar', data.filter(e => e.id !== id));
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Financial Calendar</h1>
          <p className="text-slate-400 text-sm mt-0.5">Bills, income, meetings, and vest events</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex bg-slate-800 border border-slate-700 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('month')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                viewMode === 'month' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <CalendarDays size={13} /> Month
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                viewMode === 'list' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <LayoutList size={13} /> List
            </button>
          </div>
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-lg transition-colors"
          >
            <Plus size={15} /> Add Event
          </button>
        </div>
      </div>

      {/* Cash flow summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Monthly Income', value: fmt(monthlyIncome),                      color: 'text-emerald-400' },
          { label: 'Monthly Bills',  value: fmt(Math.abs(monthlyBills)),              color: 'text-rose-400' },
          { label: 'RSU Vest',       value: fmt(rsuVests),                            color: 'text-purple-400' },
          { label: 'Net Cash Flow',  value: fmt(monthlyIncome + monthlyBills),        color: monthlyIncome + monthlyBills >= 0 ? 'text-emerald-400' : 'text-rose-400' },
        ].map(k => (
          <div key={k.label} className="bg-slate-800 border border-slate-700 rounded-xl p-4">
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">{k.label}</div>
            <div className={`text-xl font-bold ${k.color}`}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Main content */}
      {data.length === 0 ? (
        <EmptyState
          icon="📅"
          title="No events yet"
          description="Add your bills, income, RSU vests, and financial meetings to track your cash flow."
          actionLabel="Add First Event"
          onAction={() => setShowAdd(true)}
        />
      ) : viewMode === 'month' ? (
        <MonthGrid events={data} onAddClick={openAddForDate} />
      ) : (
        /* List view */
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            {[
              { id: 'all', label: 'All Events' },
              { id: 'income',  label: 'Income' },
              { id: 'bill',    label: 'Bills' },
              { id: 'rsu',     label: 'RSU Vests' },
              { id: 'meeting', label: 'Meetings' },
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  filter === f.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-800 text-slate-400 border border-slate-700 hover:text-slate-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-500 text-sm">No {filter} events found.</div>
          ) : (
            <div className="space-y-6">
              {Object.entries(grouped).sort().map(([monthKey, events]) => (
                <div key={monthKey}>
                  <div className="flex items-center gap-3 mb-3">
                    <h2 className="font-semibold text-slate-200">{monthLabel(monthKey)}</h2>
                    <div className="flex-1 h-px bg-slate-700/50" />
                    <span className="text-xs text-slate-500">{events.length} events</span>
                  </div>
                  <div className="space-y-2">
                    {events.sort((a, b) => a.date.localeCompare(b.date)).map((ev) => {
                      const t       = TYPE_CONFIG[ev.type] || TYPE_CONFIG.bill;
                      const d       = new Date(ev.date + 'T00:00:00');
                      const isToday = ev.date === TODAY;
                      const isPast  = ev.date < TODAY;
                      return (
                        <div key={ev.id || ev.date + ev.title} className={`flex items-center gap-4 p-3.5 rounded-xl border transition-all group ${t.bg} ${isPast ? 'opacity-60' : ''}`}>
                          <div className={`text-center min-w-[44px] ${isPast ? 'text-slate-500' : 'text-slate-200'}`}>
                            <div className="text-xs text-slate-500">{d.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                            <div className={`text-lg font-bold leading-tight ${isToday ? 'text-blue-400' : ''}`}>{d.getDate()}</div>
                          </div>
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${t.dot}`} />
                          <div className={`flex items-center gap-1 text-xs font-medium ${t.color} min-w-[70px]`}>
                            {t.icon}<span>{t.label}</span>
                          </div>
                          <div className="flex-1">
                            <div className={`text-sm font-medium ${isPast ? 'text-slate-400' : 'text-slate-200'}`}>{ev.title}</div>
                            {ev.note && <div className="text-xs text-slate-500 mt-0.5">{ev.note}</div>}
                            {ev.recurrence && <div className="text-xs text-slate-600 mt-0.5">{ev.recurrence}</div>}
                          </div>
                          {ev.amount !== 0 && (
                            <div className={`text-sm font-bold flex-shrink-0 ${ev.amount > 0 ? 'text-emerald-400' : 'text-slate-300'}`}>
                              {ev.amount > 0 ? '+' : '-'}{fmt(ev.amount)}
                            </div>
                          )}
                          {ev.id && (
                            <button onClick={() => handleDelete(ev.id)} className="opacity-0 group-hover:opacity-100 p-1 text-slate-600 hover:text-rose-400 transition-all">
                              <X size={14} />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add Event Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && setShowAdd(false)}>
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between px-6 pt-6 pb-4">
              <h2 className="text-lg font-bold text-slate-100">Add Event</h2>
              <button onClick={() => setShowAdd(false)} className="text-slate-500 hover:text-slate-300 p-1 rounded-lg hover:bg-slate-800"><X size={18} /></button>
            </div>
            <div className="px-6 pb-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">Date</label>
                  <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-slate-200 focus:outline-none focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">Type</label>
                  <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-slate-200 focus:outline-none focus:border-blue-500">
                    <option value="bill">🔴 Bill</option>
                    <option value="income">🟢 Income</option>
                    <option value="rsu">🟣 RSU Vest</option>
                    <option value="meeting">🔵 Meeting</option>
                    <option value="goal">🎯 Goal</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Title</label>
                <input type="text" placeholder="e.g. Netflix, Paycheck, Dentist" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3.5 py-2.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">Amount (negative = expense)</label>
                  <input type="number" placeholder="e.g. -89 or 7600" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">Recurrence</label>
                  <select value={form.recurrence} onChange={e => setForm(f => ({ ...f, recurrence: e.target.value }))}
                    className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-slate-200 focus:outline-none focus:border-blue-500">
                    <option value="">One-time</option>
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Biweekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="annual">Annual</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">Note (optional)</label>
                <input type="text" placeholder="Any context..." value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                  className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3.5 py-2.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500" />
              </div>
              <button onClick={() => handleAdd()} disabled={!form.title.trim()}
                className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-blue-800 text-white font-semibold py-2.5 rounded-xl transition-all text-sm mt-2">
                Add Event
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
